// Content config for the programmatic /tattoo-ideas/:slug SEO landing pages.
// Each entry drives one keyword-targeted page (title/meta, H1, copy, FAQ schema).
// Add a new object here to publish a new page — then add its URL to public/sitemap.xml.

export const TATTOO_IDEAS = [
  {
    slug: 'koi-fish',
    name: 'Koi Fish',
    h1: 'Koi Fish Tattoo Ideas',
    title: 'Koi Fish Tattoo Ideas — Preview Them on Your Body in 3D | KoiCreate',
    description:
      'Explore koi fish tattoo ideas and see exactly how they look on your body with a free 3D preview. Design with AI or upload your own, no signup to start.',
    intro:
      'Koi fish tattoos symbolise perseverance, transformation and good fortune, which is why they shine as flowing, large-scale pieces. Before you commit, see how a koi design wraps around your arm, back or leg with a realistic 3D preview.',
    points: [
      'Best placements: full sleeve, forearm, back and thigh, where the koi has room to flow.',
      'Pairs beautifully with water, waves and lotus elements for a classic Japanese-inspired look.',
      'Generate koi variations with AI or upload an artist’s design, then preview it to scale.',
    ],
    faqs: [
      { q: 'Where do koi fish tattoos look best?', a: 'Koi designs suit larger, flowing placements like the forearm, full sleeve, back and thigh. Use the 3D preview to test each spot before you decide.' },
      { q: 'What does a koi fish tattoo mean?', a: 'Koi commonly represent perseverance, strength and transformation, drawn from the legend of the koi that swims upstream to become a dragon.' },
      { q: 'Can I preview a koi tattoo on my own body?', a: 'Yes. Add a koi design in KoiCreate and preview it on a realistic 3D model from every angle, completely free.' },
    ],
  },
  {
    slug: 'rose',
    name: 'Rose',
    h1: 'Rose Tattoo Ideas',
    title: 'Rose Tattoo Ideas — See Them on Your Body in 3D | KoiCreate',
    description:
      'Browse rose tattoo ideas and preview them on a realistic 3D body before you get inked. Free to use, design with AI or upload your own artwork.',
    intro:
      'Roses are one of the most versatile tattoo subjects, working in everything from bold traditional to delicate fine-line styles. Preview your rose on your body first so the size and placement feel right.',
    points: [
      'Works at any scale: a small single rose on the wrist, or a full rose sleeve.',
      'Great for the forearm, shoulder, hand and ribs.',
      'Try colour vs black-and-grey versions and preview both in 3D.',
    ],
    faqs: [
      { q: 'What size should a rose tattoo be?', a: 'Roses work from tiny wrist pieces to large sleeves. Preview a few sizes on your body in KoiCreate to see which balances best.' },
      { q: 'Do rose tattoos suit black and grey?', a: 'Yes. Roses look striking in both vivid colour and black-and-grey. You can preview either style on the 3D model.' },
      { q: 'Where do rose tattoos look good?', a: 'Popular placements include the forearm, shoulder, hand, ribs and thigh.' },
    ],
  },
  {
    slug: 'fine-line',
    name: 'Fine-Line',
    h1: 'Fine-Line Tattoo Ideas',
    title: 'Fine-Line Tattoo Ideas — Preview Delicate Designs in 3D | KoiCreate',
    description:
      'Discover fine-line tattoo ideas and preview these delicate designs on your body in 3D. Free tattoo visualizer, no signup required to start.',
    intro:
      'Fine-line tattoos use thin, precise linework for a subtle, elegant result that suits smaller placements. Because detail can get lost if a piece is too small, previewing to scale is especially useful.',
    points: [
      'Ideal for the wrist, forearm, ankle, collarbone and behind the ear.',
      'Perfect for minimalist florals, script and small symbolic designs.',
      'Preview at true size to make sure the fine detail reads clearly.',
    ],
    faqs: [
      { q: 'Do fine-line tattoos age well?', a: 'Fine-line work can be beautiful but thin lines may soften over time. Choosing a size that keeps detail clear helps, and the 3D preview lets you test scale first.' },
      { q: 'Where do fine-line tattoos look best?', a: 'They suit smaller, flatter areas like the wrist, forearm, ankle and collarbone.' },
      { q: 'Can I preview a fine-line tattoo before getting it?', a: 'Yes. Upload or generate a design in KoiCreate and preview it on your body in 3D for free.' },
    ],
  },
  {
    slug: 'japanese',
    name: 'Japanese',
    h1: 'Japanese Tattoo Ideas',
    title: 'Japanese (Irezumi) Tattoo Ideas — Preview in 3D | KoiCreate',
    description:
      'Explore Japanese tattoo ideas, from koi and dragons to waves and florals, and preview them on your body in 3D. Free to use, no signup to start.',
    intro:
      'Japanese tattooing (irezumi) is known for bold subjects and flowing compositions that follow the body, like dragons, koi, tigers and waves. These large pieces reward planning, so previewing the flow on your body is invaluable.',
    points: [
      'Built for large placements: full sleeves, back pieces and leg sleeves.',
      'Common subjects: dragons, koi, tigers, snakes, peonies and waves.',
      'Preview how the composition wraps and flows across the body in 3D.',
    ],
    faqs: [
      { q: 'What are common Japanese tattoo subjects?', a: 'Dragons, koi, tigers, snakes, phoenixes, peonies and waves are all classic irezumi motifs.' },
      { q: 'Where do Japanese tattoos work best?', a: 'They are designed to flow across large areas like sleeves, the back and legs.' },
      { q: 'Can I plan a Japanese sleeve with KoiCreate?', a: 'Yes. Add your designs and preview how they sit and flow on a 3D body from every angle, for free.' },
    ],
  },
  {
    slug: 'traditional',
    name: 'Traditional',
    h1: 'Traditional Tattoo Ideas',
    title: 'Traditional (Old School) Tattoo Ideas — Preview in 3D | KoiCreate',
    description:
      'Browse traditional and old-school tattoo ideas and preview them on your body in 3D. Free tattoo visualizer, design with AI or upload your own.',
    intro:
      'Traditional (old-school) tattoos use bold outlines, a limited colour palette and timeless icons like anchors, swallows, roses and daggers. Their strong, readable shapes look great almost anywhere.',
    points: [
      'Bold lines read well at almost any size or placement.',
      'Classic icons: anchors, swallows, roses, daggers, hearts and panthers.',
      'Preview single pieces or build a bold traditional sleeve.',
    ],
    faqs: [
      { q: 'What defines a traditional tattoo?', a: 'Bold black outlines, a limited colour palette and iconic imagery like swallows, roses, anchors and daggers.' },
      { q: 'Do traditional tattoos age well?', a: 'Yes, the bold lines and solid colour tend to hold up well over time.' },
      { q: 'Can I preview a traditional tattoo on my body?', a: 'Absolutely. Add a design in KoiCreate and preview it in 3D from every angle for free.' },
    ],
  },
  {
    slug: 'minimalist',
    name: 'Minimalist',
    h1: 'Minimalist Tattoo Ideas',
    title: 'Minimalist Tattoo Ideas — Preview Small Tattoos in 3D | KoiCreate',
    description:
      'Discover minimalist and small tattoo ideas and preview them on your body in 3D before you commit. Free to use, no signup required.',
    intro:
      'Minimalist tattoos strip a design back to its simplest, cleanest form, which makes placement and size everything. Previewing on your body helps you find the spot where a small, simple piece feels intentional.',
    points: [
      'Perfect for small, discreet placements: wrist, ankle, finger, behind the ear.',
      'Great for simple symbols, tiny florals, dates and single words.',
      'Preview at true size so a small tattoo doesn’t end up smaller than expected.',
    ],
    faqs: [
      { q: 'Where should I put a small minimalist tattoo?', a: 'Popular spots include the wrist, forearm, ankle, finger and behind the ear. Preview a few in KoiCreate to compare.' },
      { q: 'How small can a tattoo be?', a: 'Very small, but fine detail can blur if it’s too tiny. Previewing to scale helps you pick a size that stays crisp.' },
      { q: 'Is KoiCreate good for planning small tattoos?', a: 'Yes, it’s ideal for testing exactly how a small design looks and sits on your body, for free.' },
    ],
  },
  {
    slug: 'snake',
    name: 'Snake',
    h1: 'Snake Tattoo Ideas',
    title: 'Snake Tattoo Ideas — Preview Them on Your Body in 3D | KoiCreate',
    description:
      'Explore snake tattoo ideas and see how they wrap around your arm or leg with a free 3D preview. Design with AI or upload your own artwork.',
    intro:
      'Snake tattoos are endlessly adaptable, symbolising rebirth, protection and transformation, and their long, curving bodies are made to wrap the arm or leg. Previewing the wrap in 3D is the best way to nail the flow.',
    points: [
      'Naturally suits wrapping placements: forearm, upper arm, calf and thigh.',
      'Works in fine-line, traditional and Japanese styles alike.',
      'Preview how the snake curves around the limb from every angle.',
    ],
    faqs: [
      { q: 'What does a snake tattoo symbolise?', a: 'Snakes are often linked to rebirth, transformation, protection and temptation, depending on the style and culture.' },
      { q: 'Where do snake tattoos look best?', a: 'Their long bodies suit wrapping placements like the forearm, upper arm and calf.' },
      { q: 'Can I preview how a snake wraps my arm?', a: 'Yes. Add a snake design in KoiCreate and rotate the 3D model to see the full wrap, for free.' },
    ],
  },
  {
    slug: 'butterfly',
    name: 'Butterfly',
    h1: 'Butterfly Tattoo Ideas',
    title: 'Butterfly Tattoo Ideas — Preview Them on Your Body in 3D | KoiCreate',
    description:
      'Browse butterfly tattoo ideas and preview them on a realistic 3D body before you get inked. Free tattoo visualizer, no signup to start.',
    intro:
      'Butterfly tattoos are a timeless symbol of change and freedom, and they flatter a wide range of placements and sizes. Preview yours first to choose the size and spot that feels right.',
    points: [
      'Flexible placements: shoulder, back, forearm, spine and ankle.',
      'Beautiful in fine-line, realistic or bold traditional styles.',
      'Preview symmetry and placement on the 3D model before committing.',
    ],
    faqs: [
      { q: 'What does a butterfly tattoo mean?', a: 'Butterflies commonly symbolise transformation, freedom, hope and new beginnings.' },
      { q: 'Where do butterfly tattoos look best?', a: 'Popular placements include the shoulder, back, forearm, spine and ankle.' },
      { q: 'Can I preview a butterfly tattoo on my body?', a: 'Yes. Add a butterfly design in KoiCreate and preview it in 3D from every angle, completely free.' },
    ],
  },
];

// Short descriptive clauses used to compose the blended (style × placement) pages.
const STYLE_BLEND = {
  'koi-fish': 'with their long, flowing bodies',
  rose: 'with their versatile, timeless shape',
  'fine-line': 'with their delicate, precise linework',
  japanese: 'with their bold, flowing compositions',
  traditional: 'with their bold outlines and iconic imagery',
  minimalist: 'with their clean, simple forms',
  snake: 'with their long, curving bodies',
  butterfly: 'with their symmetrical, elegant wings',
};

// Body-placement pages. `blend` is a clause (no leading "the ...") used to
// compose the blended pages, e.g. "the forearm is <blend>".
export const PLACEMENTS = [
  {
    slug: 'forearm',
    name: 'Forearm',
    h1: 'Forearm Tattoo Ideas',
    title: 'Forearm Tattoo Ideas — Preview Them on Your Body in 3D | KoiCreate',
    description:
      'Explore forearm tattoo ideas and preview them on a realistic 3D body before you commit. Free to use, design with AI or upload your own.',
    intro:
      'The forearm is one of the most popular tattoo placements — flat, highly visible and easy to show off or cover up. Preview your design here to get the size and position exactly right.',
    points: [
      'Great as a standalone piece or the start of a sleeve.',
      'Suits lettering, florals, animals and portraits alike.',
      'Preview inner vs outer forearm to see which suits your design.',
    ],
    faqs: [
      { q: 'Do forearm tattoos hurt?', a: 'The outer forearm is generally one of the less painful placements, while the inner forearm can be more sensitive.' },
      { q: 'What tattoos look good on the forearm?', a: 'Lettering, florals, animals and koi all work well. Preview yours in KoiCreate to check the fit.' },
      { q: 'Can I preview a forearm tattoo before getting it?', a: 'Yes. Add a design in KoiCreate and preview it on the forearm from every angle, for free.' },
    ],
    blend: "a flat, highly visible spot that's easy to show off",
  },
  {
    slug: 'sleeve',
    name: 'Sleeve',
    h1: 'Sleeve Tattoo Ideas',
    title: 'Sleeve Tattoo Ideas — Plan Your Sleeve in 3D | KoiCreate',
    description:
      'Plan a tattoo sleeve and preview how it wraps your arm in realistic 3D. Free tattoo visualizer, no signup to start.',
    intro:
      'A sleeve turns the whole arm into one flowing composition, which makes planning the layout essential. Preview how each piece connects and wraps around the arm before you commit.',
    points: [
      'Ideal for large, connected themes like Japanese or floral.',
      'Preview how pieces flow and wrap around the full arm.',
      'Build it up piece by piece and see it come together.',
    ],
    faqs: [
      { q: 'How do I plan a tattoo sleeve?', a: 'Start with a theme, then arrange pieces so they flow around the arm. KoiCreate lets you preview the whole layout in 3D.' },
      { q: 'How long does a sleeve take?', a: 'Sleeves are usually built over multiple sessions. Planning the layout first helps it come together cohesively.' },
      { q: 'Can I preview a full sleeve?', a: 'Yes. Add your designs and rotate the 3D model to see the complete wrap, for free.' },
    ],
    blend: 'part of a flowing sleeve that wraps the whole arm',
  },
  {
    slug: 'back',
    name: 'Back',
    h1: 'Back Tattoo Ideas',
    title: 'Back Tattoo Ideas — Preview Large Pieces in 3D | KoiCreate',
    description:
      'Explore back tattoo ideas and preview large pieces on a realistic 3D body. Free, design with AI or upload your own artwork.',
    intro:
      'The back is the largest, most open canvas on the body — perfect for bold, detailed statement pieces. Preview your design at full scale to plan a composition that fits.',
    points: [
      'Room for large, detailed and symmetrical designs.',
      'Great for Japanese, floral and religious pieces.',
      'Preview full-back vs upper-back placement in 3D.',
    ],
    faqs: [
      { q: 'What tattoos suit the back?', a: "Large, detailed and symmetrical designs like Japanese pieces, florals and wings all suit the back's open canvas." },
      { q: 'Do back tattoos hurt?', a: 'Pain varies; areas over the spine and shoulder blades can be more sensitive.' },
      { q: 'Can I preview a back tattoo?', a: "Yes. Preview your design on the 3D model's back from every angle, completely free." },
    ],
    blend: 'the largest and most open canvas on the body',
  },
  {
    slug: 'chest',
    name: 'Chest',
    h1: 'Chest Tattoo Ideas',
    title: 'Chest Tattoo Ideas — Preview Them in 3D | KoiCreate',
    description:
      'Browse chest tattoo ideas and preview them on a realistic 3D body before you commit. Free tattoo visualizer, no signup to start.',
    intro:
      'Chest tattoos make a bold, personal statement and lend themselves to strong, symmetrical designs. Preview how yours sits across the chest before you commit.',
    points: [
      'Suits bold, symmetrical and lettering-based designs.',
      'Can stand alone or connect into a sleeve.',
      'Preview placement and symmetry in 3D.',
    ],
    faqs: [
      { q: 'Do chest tattoos hurt?', a: 'The chest, especially near the sternum, can be a more sensitive placement.' },
      { q: 'What tattoos look good on the chest?', a: 'Bold symmetrical pieces, lettering and animals work well. Preview yours in KoiCreate.' },
      { q: 'Can I preview a chest tattoo?', a: 'Yes, on a realistic 3D model, from every angle, for free.' },
    ],
    blend: 'a bold, symmetrical placement close to the heart',
  },
  {
    slug: 'leg',
    name: 'Leg',
    h1: 'Leg Tattoo Ideas',
    title: 'Leg & Thigh Tattoo Ideas — Preview Them in 3D | KoiCreate',
    description:
      'Explore leg and thigh tattoo ideas and preview them on a realistic 3D body. Free, design with AI or upload your own.',
    intro:
      'The legs offer plenty of length and space, from the thigh to the calf, making them ideal for flowing or large designs. Preview how yours sits and wraps before you commit.',
    points: [
      'Thigh and calf both offer generous space.',
      'Great for flowing, vertical and large designs.',
      'Preview how the design wraps the leg in 3D.',
    ],
    faqs: [
      { q: 'Do leg tattoos hurt?', a: 'The outer thigh and calf are generally more forgiving, while areas near bone can be more sensitive.' },
      { q: 'What tattoos suit the leg?', a: 'Flowing, vertical and large pieces work well on the thigh and calf.' },
      { q: 'Can I preview a leg tattoo?', a: 'Yes. Preview it on the 3D model from every angle, free.' },
    ],
    blend: 'a long placement with plenty of room for flowing designs',
  },
  {
    slug: 'hand',
    name: 'Hand',
    h1: 'Hand Tattoo Ideas',
    title: 'Hand Tattoo Ideas — Preview Them in 3D | KoiCreate',
    description:
      'Browse hand tattoo ideas and preview them on a realistic 3D body before you commit. Free tattoo visualizer, no signup to start.',
    intro:
      'Hand tattoos are striking and always on show, which makes getting the size and placement right especially important. Preview yours before you commit to this bold spot.',
    points: [
      'A bold, always-visible statement placement.',
      'Suits smaller symbols, lettering and fine-line work.',
      'Preview to nail the scale on a small, visible area.',
    ],
    faqs: [
      { q: 'Do hand tattoos fade faster?', a: 'Hands see a lot of use and sun, so they can fade faster and may need touch-ups.' },
      { q: 'What tattoos suit the hand?', a: 'Smaller symbols, lettering and fine-line designs work well.' },
      { q: 'Can I preview a hand tattoo?', a: 'Yes, on a realistic 3D model, for free.' },
    ],
    blend: 'a striking, always-visible placement',
  },
  {
    slug: 'ribs',
    name: 'Ribs',
    h1: 'Rib Tattoo Ideas',
    title: 'Rib Tattoo Ideas — Preview Them in 3D | KoiCreate',
    description:
      'Explore rib tattoo ideas and preview them on a realistic 3D body. Free, design with AI or upload your own artwork.',
    intro:
      "Rib tattoos follow the body's natural curve and make an elegant, personal placement. Because the area curves, previewing in 3D helps you see how a design really sits.",
    points: [
      "An elegant placement that follows the body's curve.",
      'Great for lettering, florals and flowing designs.',
      'Preview how the design curves along the ribs in 3D.',
    ],
    faqs: [
      { q: 'Do rib tattoos hurt?', a: 'Ribs are one of the more sensitive placements, as the skin is thin over bone.' },
      { q: 'What tattoos suit the ribs?', a: 'Lettering, florals and flowing vertical designs sit beautifully along the ribs.' },
      { q: 'Can I preview a rib tattoo?', a: 'Yes. Preview it on the 3D model from every angle, free.' },
    ],
    blend: "an elegant placement that follows the body's curve",
  },
];

// Everything that has a standalone /tattoo-ideas/:slug page.
export const STANDALONE_IDEAS = [...TATTOO_IDEAS, ...PLACEMENTS];

// ---- Page builders (return a normalised object the template renders) --------

const styleToPage = (s) => ({
  ...s,
  path: `/tattoo-ideas/${s.slug}`,
  crumbLabel: `${s.name} tattoos`,
  parent: null,
  relatedTitle: `${s.name} tattoos by placement`,
  related: PLACEMENTS.map((p) => ({
    label: `${s.name} ${p.name.toLowerCase()} tattoos`,
    to: `/tattoo-ideas/${s.slug}/${p.slug}`,
  })),
});

const placementToPage = (p) => ({
  ...p,
  path: `/tattoo-ideas/${p.slug}`,
  crumbLabel: `${p.name} tattoos`,
  parent: null,
  relatedTitle: `${p.name} tattoos by style`,
  related: TATTOO_IDEAS.map((s) => ({
    label: `${s.name} ${p.name.toLowerCase()} tattoos`,
    to: `/tattoo-ideas/${s.slug}/${p.slug}`,
  })),
});

export const getIdea = (slug) => {
  const s = TATTOO_IDEAS.find((i) => i.slug === slug);
  if (s) return styleToPage(s);
  const p = PLACEMENTS.find((i) => i.slug === slug);
  if (p) return placementToPage(p);
  return null;
};

// Blended style × placement page, composed from the two entries.
export const getBlend = (styleSlug, placementSlug) => {
  const s = TATTOO_IDEAS.find((i) => i.slug === styleSlug);
  const p = PLACEMENTS.find((i) => i.slug === placementSlug);
  if (!s || !p) return null;

  const sl = s.name.toLowerCase();
  const pl = p.name.toLowerCase();
  const blend = STYLE_BLEND[s.slug] || '';

  return {
    name: `${s.name} ${p.name}`,
    h1: `${s.name} ${p.name} Tattoo Ideas`,
    title: `${s.name} ${p.name} Tattoos — Preview in 3D | KoiCreate`,
    description: `See how a ${sl} tattoo looks on the ${pl} with a free 3D preview. Design with AI or upload your own, no signup to start.`,
    intro: `${s.name} tattoos, ${blend}, are a natural fit for the ${pl} — ${p.blend}. Preview exactly how a ${sl} design sits and scales on your ${pl} before you commit, free and in realistic 3D.`,
    points: [
      `Why it works: the ${pl} is ${p.blend}.`,
      `${s.name} designs ${blend} sit naturally on the ${pl}.`,
      `Preview at true size so the placement and scale feel right.`,
    ],
    faqs: [
      { q: `Do ${sl} tattoos look good on the ${pl}?`, a: `Yes — the ${pl} is ${p.blend}, which makes it a great fit for ${sl} designs. Preview it in KoiCreate to be sure.` },
      { q: `How big should a ${sl} ${pl} tattoo be?`, a: `It depends on the detail in the design. Preview a few sizes on the 3D model to find one that stays crisp and balanced on the ${pl}.` },
      { q: `Can I preview a ${sl} tattoo on my ${pl}?`, a: `Yes. Add your design in KoiCreate and preview it on the ${pl} from every angle, completely free.` },
    ],
    path: `/tattoo-ideas/${s.slug}/${p.slug}`,
    crumbLabel: `on the ${pl}`,
    parent: { label: `${s.name} tattoos`, to: `/tattoo-ideas/${s.slug}` },
    relatedTitle: `More ${sl} tattoo placements`,
    related: PLACEMENTS.filter((x) => x.slug !== p.slug).map((x) => ({
      label: `${s.name} ${x.name.toLowerCase()} tattoos`,
      to: `/tattoo-ideas/${s.slug}/${x.slug}`,
    })),
  };
};
